import React, { useEffect, useState } from "react";
import {
  Container,
  Form,
  Button,
  Card,
  Row,
  Col,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import { HelpModal } from "../../common/compoents/HelpModal";
import { IoMdClose, IoMdPlay } from "react-icons/io";
import { FaCopy } from "react-icons/fa";
import { FAQ } from "../types";
import { FaqsGeneratorResponse } from "../../../services/getAiResponse";
import "./FaqsGenerator.css";
import { isInIframe } from "../../../utils/is-in-iframe";
import { ToolsHeader } from "../../components/ToolsHeader";
import { Box } from "./FaqsGeneratorStyle"


import {
  validateHookTopic,
  isMeaningfulText,
  isValidKeywordsInput,
} from "../../../utils/validators";

export const FaqsGenerator: React.FC = () => {
  const [topic, setTopic] = useState<string>("");
  const [keyword, setKeyword] = useState<string[]>([]);
  const [content, setContent] = useState<string>("");
  const [numberOfFaqs, setNumberOfFaqs] = useState<number>(3);
  const [language, setLanguage] = useState<string>("English");
  const [tone, setTone] = useState<string>("Professional");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [faqs, setFaqs] = useState<any[]>([]);
  const [error, setError] = useState<string>("");
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);
  const [copyMessage, setCopyMessage] = useState<string>("");
  const [newTag, setNewTag] = useState("");

  const [fieldErrors, setFieldErrors] = useState<{
    topic: string | null;
    keywords: string | null;
    content: string | null;
  }>({
    topic: null,
    keywords: null,
    content: null,
  });

  useEffect(() => {
    if (!faqs || faqs.length == 0) {
      return;
    }
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [faqs]);

  const validateBeforeSubmit = () => {
    const topicErr = validateHookTopic(topic);

    const keywordsStr = keyword.join(", ").trim();
    let keywordsErr: string | null = null;
    if (keywordsStr) {
      if (!isValidKeywordsInput(keywordsStr)) {
        keywordsErr = "Keywords look invalid or too short/repetitive";
      }
    }

    let contentErr: string | null = null;
    if (content.trim()) {
      if (content.trim().length < 40) {
        contentErr = "Content too short (min ~40 chars)";
      } else if (!isMeaningfulText(content)) {
        contentErr = "Content doesn't look meaningful";
      }
    }

    setFieldErrors({
      topic: topicErr,
      keywords: keywordsErr,
      content: contentErr,
    });

    if (topicErr) {
      setError(topicErr);
      return false;
    }

    return true;
  };

  const handleGenerateFAQs = async () => {
    if (!validateBeforeSubmit()) {
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const response = await FaqsGeneratorResponse(
        topic,
        keyword.join(", "),
        content,
        language,
        numberOfFaqs,
        tone
      );

      if (response && response.trim().length > 0) {
        const faqsArray = JSON.parse(response);
        setFaqs(faqsArray);

        const height = document.body.scrollHeight;
        window.parent.postMessage({ type: "setHeight", height }, "*");
      } else {
        setError("No FAQs were returned. Please try again.");
      }
    } catch (error) {
      console.error("Error generating FAQs:", error);
      setError("An error occurred while generating FAQs.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = (text?: string) => {
    if (text === "focus" && newTag.trim() !== "" && keyword.length < 10) {
      setKeyword([...keyword, newTag.trim()]);
      setNewTag("");
    }
  };

  const handleRemoveTag = (index: number, text?: string) => {
    if (keyword[index] && text === "focus") {
      setKeyword(keyword.filter((_, i) => i !== index));
    }
  };

  const handleReset = () => {
    setTopic("");
    setKeyword([]);
    setContent("");
    setNumberOfFaqs(3);
    setLanguage("English");
    setTone("Professional");
    setFaqs([]);
    setError("");
    setCopyMessage("");
    setNewTag("");
    setFieldErrors({ topic: null, keywords: null, content: null });
  };

  const closeVideoModal = () => setShowVideoModal(false);

  const helpSteps = [
    "Enter the main topic or query you'd like to generate FAQs for in the provided input field.",
    "Specify the number of FAQs you want to generate or leave it for automatic selection.",
    "Click the Generate FAQ button to initiate the process.",
    "Review the generated FAQs and adjust or customize the answers if needed.",
    "Copy or save the generated FAQs for use on your website or documentation.",
  ];

  const copyToClipboard = () => {
    const faqText = faqs
      .map((faq) => `${faq.question}\n${faq.answer}`)
      .join("\n\n");

    navigator.clipboard.writeText(faqText).then(
      () => {
        setCopyMessage("FAQs copied successfully!");
        setTimeout(() => setCopyMessage(""), 3000);
      },
      (err) => {
        setCopyMessage("Failed to copy FAQs. Please try again.");
        setTimeout(() => setCopyMessage(""), 3000);
      }
    );
  };

  return (
    <Box className={!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"}>
      <Container
        fluid
        className={`${!isInIframe ? "mt-5" : "mt-0"} d-flex justify-content-center`}
      >
        <div style={{ width: "100%" }}>
          <HelpModal
            helpSteps={helpSteps}
            show={showVideoModal}
            close={closeVideoModal}
            videoUrl="https://www.youtube.com/embed/hNce9hFpBOY"
          />
          {!isInIframe && (
            <>
              <ToolsHeader
                title="AI FAQ Generator"
                description="The smartest tool to generate relevant FAQs automatically based
                on your topic or content."
              />
            </>
          )}
          <Card className="p-4 shadow">
            <Form>
              {/* Topic Field */}
              <Form.Group className="mb-3">
                <Form.Label>
                  FAQ Topic <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your topic here..."
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  isInvalid={!!fieldErrors.topic || !!error}
                />
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.topic || error || "Topic is required"}
                </Form.Control.Feedback>
              </Form.Group>

              {/* Keywords */}
              <Form.Group className="mb-3">
                <Form.Label>Keywords</Form.Label>
                <div className="mb-3">
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {keyword.map((tag, index) => (
                      <div
                        key={index}
                        className="border rounded px-2 py-1 d-flex align-items-center"
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        <span className="me-3">{tag}</span>
                        <IoMdClose
                          style={{ cursor: "pointer" }}
                          onClick={() => handleRemoveTag(index, "focus")}
                        />
                      </div>
                    ))}
                  </div>
                  <Form.Control
                    type="text"
                    placeholder="Enter your keyword here..."
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddTag("focus")}
                    isInvalid={!!fieldErrors.keywords}
                  />
                  {fieldErrors.keywords && (
                    <div className="invalid-feedback d-block">
                      {fieldErrors.keywords}
                    </div>
                  )}
                </div>
              </Form.Group>

              {/* Content Field */}
              <Form.Group className="mb-3">
                <Form.Label>Content (Optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="You can add content related to your topic..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  isInvalid={!!fieldErrors.content}
                />
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.content}
                </Form.Control.Feedback>
              </Form.Group>

              {/* Number of FAQs */}
              <Form.Group className="mb-3">
                <Form.Label>Number of FAQs</Form.Label>
                <InputGroup>
                  <Form.Range
                    min={1}
                    max={10}
                    value={numberOfFaqs}
                    onChange={(e) =>
                      setNumberOfFaqs(parseInt(e.target.value, 10))
                    }
                  />
                  <Form.Control
                    type="number"
                    value={numberOfFaqs}
                    onChange={(e) =>
                      setNumberOfFaqs(parseInt(e.target.value, 10))
                    }
                    min={1}
                    max={10}
                  />
                </InputGroup>
              </Form.Group>

              {/* Language and Tone */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Language</Form.Label>
                    <Form.Select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                    >
                      <option value="English">English</option>
                      <option value="Spanish">Spanish</option>
                      <option value="French">French</option>
                      <option value="Hindi">Hindi</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tone</Form.Label>
                    <Form.Select
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                    >
                      <option value="Professional">Professional</option>
                      <option value="Casual">Casual</option>
                      <option value="Friendly">Friendly</option>
                      <option value="Formal">Funny</option>
                      <option value="Witty">Witty</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Buttons */}
              <div className="text-center mt-4">
                {isLoading ? (
                  <Spinner animation="border" role="status" />
                ) : (
                  <>
                    <Button
                      variant="primary"
                      className="me-2"
                      onClick={handleGenerateFAQs}
                    >
                      Generate FAQs
                    </Button>
                    <Button variant="secondary" onClick={handleReset}>
                      Reset
                    </Button>
                  </>
                )}
              </div>
            </Form>

            {/* Display generated FAQs */}
            {faqs.length > 0 && (
              <div className="mt-4">
                <h5>Generated FAQs</h5>
                <ul>
                  {faqs.map((faq, index) => (
                    <li key={index}>
                      <strong>{faq.question}</strong>
                      <p>{faq.answer}</p>
                    </li>
                  ))}
                </ul>

                <div className="d-flex justify-content-left mt-3">
                  <Button
                    style={{
                      backgroundColor: "#8cc540",
                      borderColor: "#8cc540",
                      color: "#fff",
                    }}
                    onClick={copyToClipboard}
                  >
                    <FaCopy className="me-2" />
                    Copy FAQs
                  </Button>
                </div>
                {copyMessage && (
                  <div className="text-left mt-3 text-success">{copyMessage}</div>
                )}
              </div>
            )}
          </Card>
        </div>
      </Container>
    </Box>
  );
};